import { create } from 'zustand'

export interface BackgroundJob {
  projectId: string
  projectName: string
  status: 'processing' | 'completed' | 'error'
  progress: number
  message: string
  startedAt: number
  type?: 'storyboard' | 'analysis'  // undefined = 後方互換でstoryboard扱い
}

const STORAGE_KEY = 'background-jobs-v1'

function loadFromStorage(): Record<string, BackgroundJob> {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (!raw) return {}
    const parsed = JSON.parse(raw) as Record<string, BackgroundJob>
    // 処理中ジョブのみ復元（24時間以内）
    const now = Date.now()
    return Object.fromEntries(
      Object.entries(parsed).filter(
        ([, job]) => job.status === 'processing' && now - job.startedAt < 24 * 60 * 60 * 1000
      )
    )
  } catch {
    return {}
  }
}

function saveToStorage(jobs: Record<string, BackgroundJob>) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(jobs))
  } catch {
    // ignore
  }
}

interface BackgroundJobStore {
  jobs: Record<string, BackgroundJob>
  /** StoryboardPage が現在マウント中の projectId（バックグラウンドポーラーのスキップ判定用） */
  mountedStoryboardProjectId: string | null
  /** AnalyzingPage が現在マウント中の projectId（バックグラウンドポーラーのスキップ判定用） */
  mountedAnalyzingProjectId: string | null
  addJob: (job: BackgroundJob) => void
  updateJob: (projectId: string, patch: Partial<BackgroundJob>) => void
  removeJob: (projectId: string) => void
  setMountedStoryboardProjectId: (projectId: string | null) => void
  setMountedAnalyzingProjectId: (projectId: string | null) => void
  getActiveJobs: () => BackgroundJob[]
}

export const useBackgroundJobStore = create<BackgroundJobStore>((set, get) => ({
  jobs: loadFromStorage(),
  mountedStoryboardProjectId: null,
  mountedAnalyzingProjectId: null,

  addJob: (job) => {
    set(s => {
      const jobs = { ...s.jobs, [job.projectId]: job }
      saveToStorage(jobs)
      return { jobs }
    })
  },

  updateJob: (projectId, patch) => {
    set(s => {
      if (!s.jobs[projectId]) return s
      const jobs = { ...s.jobs, [projectId]: { ...s.jobs[projectId], ...patch } }
      saveToStorage(jobs)
      return { jobs }
    })
  },

  removeJob: (projectId) => {
    set(s => {
      const { [projectId]: _, ...rest } = s.jobs
      saveToStorage(rest)
      return { jobs: rest }
    })
  },

  setMountedStoryboardProjectId: (projectId) => {
    set({ mountedStoryboardProjectId: projectId })
  },

  setMountedAnalyzingProjectId: (projectId) => {
    set({ mountedAnalyzingProjectId: projectId })
  },

  getActiveJobs: () => Object.values(get().jobs).filter(j => j.status === 'processing'),
}))
